import type { JssdkContextStatus } from "./context-status";

export type GetExternalContactClassification =
  | "no_customer_context"
  | "permission_denied"
  | "api_not_supported"
  | "signature_error"
  | "unknown";

export interface NormalizedWeComJsApiError {
  errMsg: string | null;
  errCode: number | string | null;
  message: string;
  raw: Record<string, unknown>;
}

const SENSITIVE_KEY_PATTERN = /secret|access[_-]?token|token|ticket|cookie|corpsecret|state/i;

export function normalizeWeComJsApiError(error: unknown): NormalizedWeComJsApiError {
  const raw = toSafeRawObject(error);
  const errMsg = firstString(raw.errMsg, raw.err_msg, raw.message, raw.error, raw.detail);
  const errCode = firstCode(raw.errCode, raw.err_code, raw.code);
  const message = errMsg ?? (typeof error === "string" ? error : "getCurExternalContact 调用失败，尚未取得足够信息判断原因。");

  return {
    errMsg,
    errCode,
    message,
    raw
  };
}

export function classifyGetCurExternalContactError(
  normalizedError: NormalizedWeComJsApiError
): GetExternalContactClassification {
  const errMsg = normalizedError.errMsg?.toLowerCase() ?? "";
  const errCode = normalizedError.errCode !== null ? String(normalizedError.errCode) : "";

  if (/without context of external contact|no external contact context|当前没有外部联系人上下文|当前入口不支持/i.test(errMsg)) {
    return "no_customer_context";
  }

  if (/no permission|permission denied/i.test(errMsg) || ["48002", "50001", "84061"].includes(errCode)) {
    return "permission_denied";
  }

  if (/function not exist/i.test(errMsg)) {
    return "api_not_supported";
  }

  if (/invalid signature|signature error|permission denied due to signature/i.test(errMsg)) {
    return "signature_error";
  }

  return "unknown";
}

export function contextStatusFromGetExternalContactClassification(
  classification: GetExternalContactClassification
): JssdkContextStatus {
  switch (classification) {
    case "no_customer_context":
      return "no_customer_context";
    case "permission_denied":
      return "permission_denied";
    case "api_not_supported":
      return "api_not_supported";
    case "signature_error":
      return "signature_error";
    case "unknown":
      return "unknown_get_external_contact_error";
  }
}

export function shouldFallbackToRawInvoke(normalizedError: NormalizedWeComJsApiError): boolean {
  return normalizedError.errMsg === null && normalizedError.errCode === null;
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value;
    }
    if (value instanceof Error && value.message.trim()) {
      return value.message;
    }
  }

  return null;
}

function firstCode(...values: unknown[]): number | string | null {
  for (const value of values) {
    if (typeof value === "number" || (typeof value === "string" && value.trim())) {
      return value;
    }
  }

  return null;
}

function toSafeRawObject(value: unknown, seen = new WeakSet<object>()): Record<string, unknown> {
  if (value === null) {
    return { value: null };
  }
  if (value === undefined) {
    return { value: "undefined" };
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return { value };
  }
  if (typeof value !== "object") {
    return { value: String(value) };
  }
  if (seen.has(value)) {
    return { value: "[Circular]" };
  }

  seen.add(value);
  const output: Record<string, unknown> = {};
  for (const key of Object.getOwnPropertyNames(value)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      output[key] = "[REDACTED]";
      continue;
    }

    try {
      output[key] = sanitizeRawValue((value as Record<string, unknown>)[key], seen);
    } catch {
      output[key] = "[Unreadable]";
    }
  }

  return output;
}

function sanitizeRawValue(value: unknown, seen: WeakSet<object>): unknown {
  if (value === null || value === undefined || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (value instanceof Error) {
    return toSafeRawObject(value, seen);
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeRawValue(item, seen));
  }
  if (typeof value === "object") {
    if (seen.has(value)) {
      return "[Circular]";
    }
    return toSafeRawObject(value, seen);
  }

  return String(value);
}
